import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { usePreferences } from "hooks/use-preferences"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useIsMultiDashboardsEnabled } from "resources/account"
import { useDashboardId } from "../context/dashboard-id"
import { panelsSlice, createDefaultModules, type PanelsState } from "../store/panels-slice"
import { useHasThematicBetasContentSet } from "./use-has-thematic-betas-content-set"
import type { DashboardModule } from "../modules/types"

const PREFERENCE_SUBTYPE = "modules"
const PREFERENCE_FIELDS = ["modules"]

/**
 * Loads dashboard panel preferences into the panelsSlice and reports `true`
 * once the slice instance for the current dashboardId is populated.
 *
 * The lazily-initialised default modules are passed to `usePreferences` as the
 * baseline state so the no-saved-prefs path captures defaults (not `[]`) when
 * it locks in the diff baseline — without that, the dispatch that populates
 * the instance one render later would look like a real "modules: [] → [defaults]"
 * change and trigger a debounced POST of the defaults to the server on first
 * load. setPreferencesAction is stabilised with useCallback so usePreferences'
 * effect dependency array does not cause infinite re-fires.
 */
export function useSyncPanelsPreferences(): boolean {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  // Instance mode: the workspace writer owns persistence, so suppress this subtype's write.
  const skipSave = useIsMultiDashboardsEnabled()
  const { hasThematicBetasContentSet, isContentSetsLoaded } = useHasThematicBetasContentSet()

  // Lazily-built baseline passed to `usePreferences` so the no-saved-prefs
  // path has a non-empty diff baseline (otherwise a later `modules: [] → [defaults]`
  // transition would look like a real change and trigger a debounced POST).
  // Content-sets entitlement isn't available at first render, so the baseline
  // is the unentitled five-default snapshot; entitled accounts pay one extra
  // POST on their very first dashboard load when the renderer adds the sixth
  // panel — acceptable trade-off vs. delaying the gate further.
  const [initialState] = useState<PanelsState>(() => ({
    modules: createDefaultModules(),
    isPreferencesLoaded: false,
  }))

  const storeState = useAppSelector((s) => s.dashboard.panels[dashboardId])
  const state = storeState ?? initialState

  // Seed-before-sync (instance mode): if the workspace loader already seeded this panels slot
  // before this hook mounted, suppress the legacy `modules` preference load — otherwise the stale
  // per-subtype module list would clobber the workspace/layout seed once the fetch settles. Mirrors
  // the core/model/trade/optimization sync hooks. Flag-off mounts unseeded → loads normally.
  const [wasInitializedAtMount] = useState(() => state.isPreferencesLoaded)

  // Transient modules (drilldown panels) are session-only — strip them before the state
  // snapshot is handed to usePreferences so they never reach the POST payload.
  const persistableState = useMemo(() =>
    state.modules.some(m => m.config?.transient)
      ? { ...state, modules: state.modules.filter(m => !m.config?.transient) }
      : state,
  [state],
  )

  // Guard against dashboardId changing mid-lifecycle. usePreferences' load
  // effect uses isInitialLoadRef which never resets, so a new dashboardId
  // would not trigger a fresh preference load, causing the new dashboard to
  // display default modules instead of the user's saved panels.
  const initialDashboardIdRef = useRef(dashboardId)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && dashboardId !== initialDashboardIdRef.current) {
      throw new Error(
        `useSyncPanelsPreferences: dashboardId changed from "${initialDashboardIdRef.current}" to "${dashboardId}". `
        + "dashboardId must be stable for the component lifetime.",
      )
    }
  }, [dashboardId])

  // `hasThematicBetasContentSet` is threaded into setPreferences so the
  // validateModules recovery branches (non-array input AND non-empty
  // all-invalid input) restore the six-default snapshot — including the
  // thematic-betas panel — when the account is entitled.
  const setPreferencesAction = useCallback(
    (prefs: { modules?: DashboardModule[] }) =>
      panelsSlice.actions.setPreferences({
        dashboardId,
        modules: prefs.modules?.filter(m => !m.config?.transient),
        hasThematicBetasContentSet,
      }),
    [dashboardId, hasThematicBetasContentSet],
  )

  const isPreferencesRequestLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    persistableState,
    dispatch,
    setPreferencesAction,
    { skipInitialLoad: wasInitializedAtMount, skipSave },
  )

  // When the user has no saved preferences, usePreferences flips its internal
  // loaded flag true WITHOUT dispatching (preferencesToApply is {}). Wait for
  // the content-sets request to settle so we can seed the correct number of
  // defaults in one shot — otherwise we'd seed five, then the renderer would
  // append the thematic-betas panel a render later, producing a visible flash.
  useEffect(() => {
    if (!isPreferencesRequestLoaded || state.isPreferencesLoaded || !isContentSetsLoaded) return
    dispatch(panelsSlice.actions.setPreferences({
      dashboardId,
      modules: createDefaultModules(hasThematicBetasContentSet),
      hasThematicBetasContentSet,
    }))
  }, [isPreferencesRequestLoaded, state.isPreferencesLoaded, isContentSetsLoaded, hasThematicBetasContentSet, dashboardId, dispatch])

  return state.isPreferencesLoaded
}
